import React, { Component } from "react"
import PropTypes from "prop-types"
import Radium, { keyframes } from "higher-order-components/configured-radium/"
import { invokeFuncs } from "utils/function"
import bowser from "bowser"

import { ErrorIcon, SearchIcon, CrossIcon, EyeIcon, InputLabel } from "components"

const renderIcon = (name, style) => {
  switch (name) {
    case "error":
      return <ErrorIcon style={style} />
    case "search":
      return <SearchIcon style={style} />
    case "cross":
      return <CrossIcon style={style} />
    case "eye":
      return <EyeIcon style={style} />
  }
}

const themeLight = {
  label: valid => ({
    color: "#d5d5d5"
  }),
  input: valid => ({
    color: valid ? "#101010" : "#910A2D",
    borderColor: valid ? "#d5d5d5" : "#910A2D"
  }),
  message: valid => ({
    color: valid ? "#999999" : "#910A2D"
  }),
  iconSvg: valid => ({
    fill: valid ? "#999" : "#910A2D"
  })
}

const themeDark = {
  label: valid => ({
    color: "#fff"
  }),
  input: valid => ({
    color: valid ? "#fff" : "#910A2D",
    borderColor: valid ? "#d5d5d5" : "#910A2D"
  }),
  message: valid => ({
    color: valid ? "#fff" : "#910A2D"
  }),
  iconSvg: valid => ({
    fill: valid ? "#fff" : "#910A2D"
  })
}

const themes = {
  dark: themeDark,
  light: themeLight
}

/**
 * @param {Object} props - Component props
 * @param {String} props.label - Field label text
 * @param {String} props.value - Input value
 * @param {Number} props.maxLength - input maxLength property
 * @param {String} props.placeholder - Field placeholder text
 * @param {Boolean} props.disabled - disable input
 * @param {String} props.icon - Name of icon
 * @param {Boolean} props.isValid - validation status of input
 * @param {String} props.message - message displayed after input
 * @param {Boolean} props.required - input required
 * @param {Function} props.onChange - onChange callback of input
 * @param {Function} props.onIconClick - Callback for when icon is clicked.
 * @param {Function} props.styleName - change the theme of the component
 * @returns {ReactElement} React Component
 */
class InputText extends Component {
  onBlurHandler = () => {
    const { onBlur, gtmEvents } = this.props
    onBlur && onBlur()
    gtmEvents && invokeFuncs(gtmEvents)
  }

  render() {
    const {
      innerRef,
      label,
      focus,
      gtmEvents,
      initialValue,
      value,
      type,
      showBorderBottom = true,
      required,
      placeholder,
      onIconClick,
      icon,
      iconText,
      message,
      isValid,
      margin,
      styleName,
      style,
      id,
      ...inputProps
    } = this.props

    const themeStyles = themes[styleName]
    const visiblePlaceholder = !label && placeholder ? placeholder : ""

    return (
      <div
        style={{
          margin: margin || "1em auto 1.9em",
          position: "relative",
          ...style
        }}
        data-test={id || "input-text"}>
        <div style={{ position: "relative" }}>
          {label && (
            <InputLabel
              text={label}
              dirty={!!(value && value.length > 0)}
              required={!inputProps.disabled && required}
              styleName={styleName}
              disabled={inputProps.disabled}
              style={styles.label}
            />
          )}
          <input
            {...inputProps}
            ref={c => {
              innerRef && innerRef(c)
              this.inputEl = c
            }}
            style={[
              styles.input,
              showBorderBottom && styles.inputBorder,
              themeStyles.input(isValid),
              { opacity: inputProps.disabled ? 0.6 : 1 },
              styles.iconPadding
            ]}
            type={type || "text"}
            placeholder={!bowser.msie ? visiblePlaceholder : ""} // Special IE placeholder implementation below
            value={value || ""}
            onBlur={this.onBlurHandler}
          />
          {bowser.msie &&
            (!value || (value && !value.length)) && (
              <div
                style={[
                  {
                    pointerEvents: "none",
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0,
                    textAlign: "left",
                    fontWeight: 200
                  },
                  styles.iconPadding
                ]}>
                {visiblePlaceholder}
              </div>
            )}
          <div style={styles.iconWrapper}>
            {icon && (
              <div
                onClick={() => onIconClick({ icon })}
                style={[styles.iconContainer, !!onIconClick && { cursor: "pointer" }]}>
                <div style={[styles.icon, { display: icon === "eye" ? "inline-block" : "block" }]}>
                  {renderIcon(icon, {
                    ...themeStyles.iconSvg(true),
                    ...styles.baseSvg
                  })}
                </div>
                {icon === "eye" && <span style={styles.iconText}>{iconText}</span>}
              </div>
            )}
            {!isValid && (
              <div style={[styles.iconContainer, styles.fadeIn]}>
                <div style={styles.icon}>
                  {renderIcon("error", {
                    ...themeStyles.iconSvg(isValid),
                    ...styles.baseSvg
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
        {message && (
          <p style={[styles.message, themeStyles.message(isValid), styles.fadeIn]}>{message}</p>
        )}
      </div>
    )
  }
}

InputText.propTypes = {
  innerRef: PropTypes.func,
  attributes: PropTypes.object,
  name: PropTypes.string,
  label: PropTypes.string,
  type: PropTypes.string,
  onChange: PropTypes.func,
  onIconClick: PropTypes.func,
  onKeyDown: PropTypes.func,
  autoComplete: PropTypes.string,
  showBorderBottom: PropTypes.bool,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  maxLength: PropTypes.number,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  icon: PropTypes.string,
  message: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  placeholder: PropTypes.string,
  isValid: PropTypes.bool,
  focus: PropTypes.bool,
  margin: PropTypes.string,
  styleName: PropTypes.oneOf(["light", "dark"]),
  gtmEvents: PropTypes.array,
  style: PropTypes.object
}

InputText.defaultProps = {
  focus: false,
  isValid: true,
  margin: "",
  styleName: "light"
}

const fadeInKeyframes = keyframes({
  from: {
    opacity: 0
  }
})

const styles = {
  baseSvg: {
    height: "100%",
    width: "100%"
  },
  fadeIn: {
    animation: "x .666s",
    animationName: fadeInKeyframes
  },
  message: {
    position: "absolute",
    fontSize: "75%",
    fontWeight: 200,
    top: "calc(100% + .6em)",
    margin: 0
  },
  iconWrapper: {
    position: "absolute",
    right: ".5em",
    top: "65%"
  },
  iconContainer: {
    float: "right",
    padding: "0px 0px 0px 3px"
  },
  icon: {
    width: "1em",
    height: "1em",
    transform: "translateY(-50%)"
  },
  input: {
    fontWeight: 200,
    WebkitAppearance: "none",
    caretColor: "currentColor",
    /* disable box shadow for firefox red shadow on page load bug */
    boxShadow: "none",
    background: "none",
    boxSizing: "border-box",
    width: "100%",
    outline: "none",
    borderRadius: 0,
    borderWidth: "0px"
  },
  inputBorder: {
    borderTop: "none",
    borderLeft: "none",
    borderRight: "none",
    borderBottomStyle: "solid",
    borderBottomWidth: "1px"
  },
  iconPadding: {
    padding: "1.5em 1.4em .75em 0"
  },
  iconText: {
    display: "inline-block",
    fontSize: "14px",
    lineHeight: "22px",
    marginLeft: "0.25em",
    transform: "translateY(-50%)"
  }
}

export default Radium(InputText)



// WEBPACK FOOTER //
// ./src/components/form-elements/input-text/index.jsx